import { useState } from 'react'
import { useNavigate } from 'react-router'

import {
    Button, Feature,
    IconDownload,
    IconX, PageHeader,
    CompanyIndexFilters
} from '@/components'
import { AuthLayout, IndexContainer } from '@/containers'
import { useAuth, useIndexContainer } from '@/hooks'
import { Company, Model } from '@/models'
import { loadingBar } from '@/services'
import {
    CompanyIndexDataType,
    IndexContainerChangeEvent,
    CompanyIndexFiltersType,
    IndexContainerFilterValueType,
    DateRangeType
} from '@/types'

export const COMPANY_INDEX_FILTERS: CompanyIndexFiltersType = {
    search: {
        label: 'Search',
        defaultValue: '',
        value: '',
        isStandalone: true
    },
    type: {
        label: 'Company Type',
        defaultValue: '',
        value: ''
    },
    conglomerate_id: {
        label: 'Affiliate',
        defaultValue: '',
        value: ''
    },
    monitoring_status: {
        label: 'Monitoring Status',
        defaultValue: '',
        value: '',
        isStandalone: true
    },
    battery_eligibility: {
        label: 'Battery Eligibility',
        defaultValue: '',
        value: ''
    },
    company_account_activity_statuses: {
        label: 'Company Status',
        defaultValue: [],
        value: [],
        isMultiple: true
    },
    account_manager_email: {
        label: 'Account Manager',
        defaultValue: '',
        value: ''
    },
    last_warranty_created_date_range: {
        label: 'Last Warranty',
        defaultValue: [null, null],
        value: [null, null],
        convert: (value: IndexContainerFilterValueType) =>
            value ? (value as [string, string]).map(Model.toDate) as DateRangeType : [null, null]
    }
}
const CompaniesIndex = () => {
    const auth = useAuth()
    const navigate = useNavigate()
    const {
        filters,
        handleFiltersChange
    } = useIndexContainer(COMPANY_INDEX_FILTERS, 'Companies')

    const [data, setData] = useState<CompanyIndexDataType>({
        meta: {
            total: 0,
            overall_count: 0,
            current_page: 0
        },
        rows: []
    })

    const handleDataChange = (e: IndexContainerChangeEvent) => {
        setData(data => ({ ...data, [e.name]: e.value }))
    }

    return <AuthLayout heading={<PageHeader title="Companies"/>}>
        <IndexContainer
            id="companies"
            dataName="Companies"
            api={(...args) => Company.index(...args)}
            data={data}
            onDataChange={handleDataChange}
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onFiltersClear={() => navigate('/companies')}
            tableOptions={{
                maxColumnsAmount: 6,
                dataName: 'Companies'
            }}
            columns={[
                { title: 'Company Name', field: 'name', cellClassName: 'max-w-[18.75rem]' },
                { title: 'Company Status', field: 'account_activity_status', sortable: false },
                { title: 'Salesforce Unique ID', field: 'unique_id' },
                { title: 'Last Warranty', field: 'last_warranty_created_at' },
                { title: 'Monitoring', field: 'monitoring_status', sortable: false },
                { title: 'Battery Eligibility', field: 'battery_eligibility' },
                { title: 'Company Type', field: 'type' },
                { title: 'Policy Rate', field: 'policy_rate' },
                { title: 'Account Manager', field: 'account_manager' },
                { title: 'Actions', field: 'actions', clickable: false, sortable: false }
            ]}
            rows={data.rows.map(item => ({
                id: item.id,
                unique_id: item.unique_id,
                name: item.name,
                account_activity_status: item.account_activity_status?.badge(),
                type: item.type?.title,
                policy_rate: item.product_rates?.power_production ? item.product_rates.power_production.money() : '',
                last_warranty_created_at: item.last_warranty_created_at?.format() || '-',
                monitoring_status: item.monitoringStatus.getBadge(),
                battery_eligibility: item.battery_eligibility?.icon('md'),
                account_manager: item.account_manager || '-',
                actions: <div className="flex items-center gap-2">
                    <Feature name="company-verification">
                        <Button design="btn-link" tooltip="Mark as Not Verified">
                            <IconX className="stroke-gray-300 hover:stroke-error-600"/>
                        </Button>
                    </Feature>
                    <Button
                        tooltip="Download System Data"
                        design="btn-link"
                        onClick={async () => {
                            loadingBar.start()
                            await item.downloadSystemData()
                            loadingBar.stop()
                        }}
                    >
                        <IconDownload className="stroke-gray-300 hover:stroke-primary-600"/>
                    </Button>
                </div>,
                _path: `/companies/${item.id}`
            }))}
            leftSide={<CompanyIndexFilters
                standalone
                filters={filters}
                onChange={handleFiltersChange}
            />}
            rightSide={<>
                {auth.user?.role.isAdminOrStaff && <Button
                    className="mt-2 lg:mt-0 text-center whitespace-nowrap"
                    href="/companies/create"
                >
                    New Account
                </Button>}
            </>}
            popupContent={<CompanyIndexFilters
                filters={filters}
                onChange={handleFiltersChange}
            />}
        />
    </AuthLayout>
}

export default CompaniesIndex
